import { useEffect, useState } from "react";
import { fetchAppearance, saveAppearance } from "../api/api";
import { useApp } from "../Contex";

// Groups instead of flat theme map
const themeGroups = [
    {
        title: "Accent",
        keys: [
            { key: "primary", label: "Bakgrund" },
            { key: "primaryText", label: "Text" }
        ]
    },
    {
        title: "Sekundär",
        keys: [
            { key: "secondary", label: "Bakgrund" },
            { key: "secondaryText", label: "Text" }
        ]
    },
    {
        title: "Block",
        keys: [
            { key: "block", label: "Bakgrund" },
            { key: "blockText", label: "Text" }
        ]
    },
    {
        title: "Bakgrund",
        keys: [
            { key: "background", label: "Bakgrund" },
            { key: "backgroundText", label: "Text" }
        ]
    },
    {
        title: "Rubrik",
        keys: [
            { key: "header", label: "Bakgrund" },
            { key: "headerText", label: "Text" }
        ]
    },
    {
        title: "Dialoger",
        keys: [
            { key: "modal", label: "Bakgrund" },
            { key: "modalText", label: "Text" }
        ]
    }
];

// Utility: normalize 3-char hex (#abc) → 6-char hex (#aabbcc)
function normalizeHex(hex) {
    if (!hex) return "#000000";
    hex = hex.startsWith("#") ? hex : `#${hex}`;
    if (hex.length === 4) {
        return (
            "#" +
            hex[1] + hex[1] +
            hex[2] + hex[2] +
            hex[3] + hex[3]
        );
    }
    return hex.toLowerCase();
}

const ColorInput = ({ themeKey, title, colour, onChange }) => {
    const normalized = normalizeHex(colour);
    const [textValue, setTextValue] = useState(normalized.replace(/^#/, "")); // store without #

    const handleColorChange = (value) => {
        const hex = normalizeHex(value);
        setTextValue(hex.replace(/^#/, ""));
        onChange(themeKey, hex);
    };

    const handleTextChange = (e) => {
        setTextValue(e.target.value); // raw typing
    };

    const handleTextCommit = () => {
        // Only commit when blur/enter
        const candidate = `#${textValue.replace(/[^0-9a-fA-F]/g, "")}`;
        if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(candidate)) {
            onChange(themeKey, normalizeHex(candidate));
        } else {
            // Reset to last known good if invalid
            setTextValue(normalized.replace(/^#/, ""));
        }
    };

    return (
        <div className="d-flex align-items-center">
            {/* Color picker */}
            <input
                type="color"
                value={normalized}
                onChange={e => handleColorChange(e.target.value)}
                style={{
                    width: "32px",
                    height: "32px",
                    border: "none",
                    padding: 0,
                    background: "none",
                    cursor: "pointer"
                }}
            />

            {/* Label */}
            <span className="ms-2">{title}</span>

            {/* Text input with "#" prefix */}
            <div className="d-flex align-items-center ms-auto" style={{ width: "110px" }}>
                <input
                    type="text"
                    value={textValue}
                    onChange={handleTextChange}
                    onBlur={handleTextCommit}
                    onKeyDown={e => e.key === "Enter" && handleTextCommit()}
                    maxLength={6}
                    className="form-control form-control-sm"
                    style={{
                        fontFamily: "monospace",
                        flex: 1,
                        marginLeft: "4px"
                    }}
                />
            </div>
        </div>
    );
};

export function AppearancePage() {
    const [data, setData] = useState({ theme: {} });       // original baseline
    const [editedData, setEditedData] = useState({ theme: {} }); // editable copy
    const [loading, setLoading] = useState(true);

    const { refresh } = useApp();

    // Fetch full appearance object
    const fetchData = async () => {
        const res = await fetchAppearance();
        if (res.ok) {
            setData(res.data);
            setEditedData(res.data);
        } else {
            setData({ theme: {} });
            setEditedData({ theme: {} });
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [refresh]);

    // Change handler for theme values
    const handleColorChange = (key, value) => {
        setEditedData(prev => ({
            ...prev,
            theme: {
                ...prev.theme,
                [key]: value
            }
        }));
    };

    // Compare objects deeply (stringify is fine for small configs like this)
    const hasChanges = JSON.stringify(data) !== JSON.stringify(editedData);

    const handleSave = () => {
        const { theme } = editedData;
        const body = { theme };
        saveAppearance(body).then((res) => {
            if (res.ok) {
                fetchData();
            }
        });
    };

    if (loading) return <div className="container"><p>Laddar...</p></div>;

    return (
        <div className="container">
            <div className="mb-2 d-flex justify-content-between">
                <h1>Utseende</h1>
                {hasChanges && (
                    <button className="btn btn-success" onClick={handleSave}>
                        <i className="bi bi-save me-2" />Spara
                    </button>
                )}
            </div>
            <div className="card card-body">
                <h3>Färgtema</h3>
                {themeGroups.map(group => (
                    <div key={group.title} className="mb-3">
                        <h5>{group.title}</h5>
                        <div className="d-flex flex-wrap gap-4">
                            {group.keys.map(item => (
                                <ColorInput
                                    key={item.key}
                                    themeKey={item.key}
                                    title={item.label}
                                    colour={editedData?.theme?.[item.key]}
                                    onChange={handleColorChange}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
